/**
 * Dexie Encryption Middleware
 *
 * Provides transparent encryption/decryption of data stored in IndexedDB.
 * Uses AES-GCM encryption with a user-specific key.
 *
 * Data flow:
 * - Write: Object → JSON → Encrypt → Base64 string stored in IndexedDB
 * - Read: Base64 string → Decrypt → JSON → Object
 *
 * This DBCore middleware encrypts the `cases` and `jurors` tables transparently.
 * The `pendingOperations` table is ALSO encrypted (its `data`/`diff` payloads
 * carry the same juror/case content), but at the repository layer
 * (see PendingRepository) rather than here: that table's read-modify-write merge
 * flow can't hold an IndexedDB transaction open across this middleware's async
 * `crypto.subtle` calls. PendingRepository reuses the `encrypt`/`decrypt`
 * exported below, doing the crypto in plain JS outside the IDB operations.
 * syncMetadata holds no sensitive data and is left plaintext.
 *
 * `crypto.subtle` returns a native Promise, not a Dexie-tracked one, so an
 * `await` on it isn't visible to Dexie's transaction zone (PSD) — the IDB
 * transaction can auto-commit while we're still awaiting encryption, causing
 * `TransactionInactiveError`/`InvalidStateError` on the subsequent read/write.
 * `encrypt`/`decrypt` below wrap the `crypto.subtle` call in `Dexie.waitFor()`,
 * which keeps the enclosing transaction alive until it resolves. Outside a
 * transaction (e.g. PendingRepository, migration) `Dexie.waitFor()` is a no-op
 * passthrough, so this is safe everywhere `encrypt`/`decrypt` are called.
 */

import Dexie from 'dexie';

// Tables that should be encrypted by this (DBCore) middleware.
const ENCRYPTED_TABLES = ['cases', 'jurors'];

// Fields to exclude from encryption (kept as-is for indexing)
const INDEX_FIELDS = {
  cases: ['_id', 'creator', 'updatedAt', 'revision'],
  jurors: ['_id', 'caseId', 'seatNumber', 'alternateSeatNumber', 'revision']
};

/**
 * Encrypt data using AES-GCM
 * @param {CryptoKey} key - Encryption key
 * @param {Object} data - Data to encrypt
 * @returns {Promise<string>} - Base64 encoded encrypted data
 */
async function encrypt(key, data) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(data));
  
  const encrypted = await Dexie.waitFor(crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoded
  ));

  // Combine IV and encrypted data
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);
  
  return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypt data using AES-GCM
 * @param {CryptoKey} key - Encryption key
 * @param {string} encryptedString - Base64 encoded encrypted data
 * @returns {Promise<Object>} - Decrypted data
 */
async function decrypt(key, encryptedString) {
  try {
    const combined = Uint8Array.from(atob(encryptedString), c => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);
    
    const decrypted = await Dexie.waitFor(crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encrypted
    ));
    
    return JSON.parse(new TextDecoder().decode(decrypted));
  } catch (e) {
    console.error('Decryption failed:', e);
    throw new Error('Failed to decrypt data');
  }
}

/**
 * Prepare object for storage by encrypting sensitive fields
 * @param {CryptoKey} key - Encryption key
 * @param {string} tableName - Table name
 * @param {Object} obj - Object to prepare
 * @returns {Promise<Object>} - Object with encrypted data
 */
async function prepareForStorage(key, tableName, obj) {
  if (!ENCRYPTED_TABLES.includes(tableName) || !key) {
    return obj;
  }

  const indexFields = INDEX_FIELDS[tableName] || [];
  
  // Separate index fields from encrypted data
  const indexData = {};
  const sensitiveData = {};

  for (const [field, value] of Object.entries(obj)) {
    if (indexFields.includes(field)) {
      indexData[field] = value;
    } else {
      sensitiveData[field] = value;
    }
  }

  // Encrypt sensitive data
  const encryptedData = await encrypt(key, sensitiveData);

  return {
    ...indexData,
    _encrypted: encryptedData
  };
}

/**
 * Restore object after reading from storage
 * @param {CryptoKey} key - Encryption key
 * @param {string} tableName - Table name
 * @param {Object} obj - Object from storage
 * @returns {Promise<Object>} - Decrypted object
 */
async function restoreFromStorage(key, tableName, obj) {
  if (!ENCRYPTED_TABLES.includes(tableName) || !key) {
    return obj;
  }

  if (!obj._encrypted) {
    // Not encrypted (legacy data or migration)
    return obj;
  }

  // Decrypt sensitive data
  const sensitiveData = await decrypt(key, obj._encrypted);

  // Merge with index fields
  const { _encrypted, ...indexData } = obj;
  
  return {
    ...indexData,
    ...sensitiveData
  };
}

/**
 * Apply encryption middleware to a Dexie database
 * Uses Dexie 4's DBCore middleware (supports async) instead of legacy hooks (synchronous only).
 * @param {Dexie} db - Dexie database instance
 * @param {CryptoKey} encryptionKey - User's encryption key
 */
export function applyEncryption(db, encryptionKey) {
  if (!encryptionKey) {
    console.warn('No encryption key provided, data will not be encrypted');
    return;
  }

  db.use({
    stack: 'dbcore',
    name: 'encryption',
    create(downlevelDatabase) {
      return {
        ...downlevelDatabase,
        table(tableName) {
          const downlevelTable = downlevelDatabase.table(tableName);

          if (!ENCRYPTED_TABLES.includes(tableName)) {
            return downlevelTable;
          }

          return {
            ...downlevelTable,

            // Encrypt before add/put, pass through deletes
            async mutate(req) {
              if (req.type === 'add' || req.type === 'put') {
                const encryptedValues = await Promise.all(
                  req.values.map(obj => prepareForStorage(encryptionKey, tableName, obj))
                );
                return downlevelTable.mutate({ ...req, values: encryptedValues });
              }
              return downlevelTable.mutate(req);
            },

            // Decrypt single-record reads. decrypt() internally pins the
            // transaction across the crypto.subtle call (see decrypt()
            // below) — that's the only IDB-sensitive part of this method, so
            // nothing further needs wrapping here. An earlier version wrapped
            // this whole method body in an *outer* Dexie.waitFor(), which
            // deadlocks: any read issued after a prior write on the same
            // table never resolves (JURYBOX-WEB-APP-7). Nested/overlapping
            // Dexie.waitFor() calls across separate top-level operations
            // don't compose safely, so keep the wrapping scoped to exactly
            // the crypto.subtle call that needs it.
            async get(req) {
              const result = await downlevelTable.get(req);
              if (!result) return result;
              return restoreFromStorage(encryptionKey, tableName, result);
            },

            // Decrypt multi-record reads
            async getMany(req) {
              const results = await downlevelTable.getMany(req);
              return Promise.all(
                results.map(r => r ? restoreFromStorage(encryptionKey, tableName, r) : r)
              );
            },

            // Decrypt query results (orderBy, where, toArray, etc.)
            async query(req) {
              const result = await downlevelTable.query(req);
              if (req.values) {
                const decrypted = await Promise.all(
                  result.result.map(r =>
                    (typeof r === 'object' && r !== null)
                      ? restoreFromStorage(encryptionKey, tableName, r)
                      : r
                  )
                );
                return { ...result, result: decrypted };
              }
              return result; // Keys only — no decryption needed
            },

            // Cursor-based iteration: IDB cursors are synchronous but
            // decryption (crypto.subtle) is async. We fetch all matching
            // data via query() (which decrypts), then return a virtual
            // cursor that iterates over the in-memory results.
            async openCursor(req) {
              // Fetch and decrypt all matching data upfront
              const rawResult = await downlevelTable.query({ ...req, values: true });
              const items = await Promise.all(
                rawResult.result.map(r =>
                  (typeof r === 'object' && r !== null)
                    ? restoreFromStorage(encryptionKey, tableName, r)
                    : r
                )
              );

              // query() doesn't handle direction — reverse in JS if needed
              if (req.reverse) items.reverse();

              if (items.length === 0) return null;

              // Determine cursor key from index or primary key
              const indexKeyPath = (req.query?.index && !req.query.index.isPrimaryKey)
                ? req.query.index.keyPath
                : null;
              const getKey = (item) => {
                if (indexKeyPath && typeof indexKeyPath === 'string') return item[indexKeyPath];
                return item._id;
              };

              let pos = 0;
              let stopped = false;

              const cursor = {
                trans: req.trans,
                key: getKey(items[0]),
                primaryKey: items[0]?._id,
                value: req.values !== false ? items[0] : undefined,
                done: false,

                start(onNext) {
                  return new Promise(resolve => {
                    const step = () => {
                      if (pos >= items.length || stopped) {
                        cursor.done = true;
                        resolve();
                        return;
                      }
                      cursor.key = getKey(items[pos]);
                      cursor.primaryKey = items[pos]?._id;
                      cursor.value = req.values !== false ? items[pos] : undefined;

                      if (onNext() === false) {
                        resolve();
                        return;
                      }
                      pos++;
                      Promise.resolve().then(step);
                    };
                    step();
                  });
                },

                continue() { /* virtual cursor — start() handles advancement */ },
                continuePrimaryKey() {},
                advance(count) { pos += (count - 1); },
                stop() { stopped = true; },
                next() {
                  pos++;
                  if (pos >= items.length) {
                    cursor.done = true;
                    return Promise.resolve({ done: true });
                  }
                  cursor.key = getKey(items[pos]);
                  cursor.primaryKey = items[pos]?._id;
                  cursor.value = req.values !== false ? items[pos] : undefined;
                  return Promise.resolve({ done: false, value: cursor.value });
                },
                fail() { stopped = true; }
              };

              return cursor;
            }
          };
        }
      };
    }
  });

  console.log('Encryption middleware applied to tables:', ENCRYPTED_TABLES);
}

/**
 * Check if data is encrypted
 * @param {Object} obj - Object to check
 * @returns {boolean}
 */
export function isEncrypted(obj) {
  return obj && typeof obj._encrypted === 'string';
}

// Low-level AES-GCM helpers, exported for callers that encrypt specific fields
// outside the DBCore middleware (e.g. PendingRepository, which must do crypto in
// plain JS so it never holds an IndexedDB transaction open across a crypto await).
export { encrypt, decrypt };

/**
 * Manually encrypt an object (for testing/migration)
 * @param {CryptoKey} key - Encryption key
 * @param {string} tableName - Table name
 * @param {Object} obj - Object to encrypt
 * @returns {Promise<Object>}
 */
export async function encryptObject(key, tableName, obj) {
  return prepareForStorage(key, tableName, obj);
}

/**
 * Manually decrypt an object (for testing/migration)
 * @param {CryptoKey} key - Encryption key
 * @param {string} tableName - Table name
 * @param {Object} obj - Object to decrypt
 * @returns {Promise<Object>}
 */
export async function decryptObject(key, tableName, obj) {
  return restoreFromStorage(key, tableName, obj);
}

export default {
  applyEncryption,
  isEncrypted,
  encryptObject,
  decryptObject
};
